import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";

const SubdivisionContext = createContext(null);

export const SubdivisionContextProvider = ({ children }) => {
  const [subdivisions, setSubdivisions] = useState(new Map());

  // Function to update a specific subdivision
  const updateSubdivision = useCallback(
    (subdivisionId, newData, expandOnlyOne = false) => {
      if (subdivisionId) {
        if (expandOnlyOne) {
          setSubdivisions((prev) => {
            const newSubdivisions = new Map(prev);
            const current = newSubdivisions.get(subdivisionId);
            const updated = {
              ...current,
              ...newData,
            };
            if (JSON.stringify(current) === JSON.stringify(updated)) {
              return prev;
            }
            newSubdivisions.set(subdivisionId, updated);

            newSubdivisions.forEach((value, id) => {
              newSubdivisions.set(id, {
                ...value,
                expanded: id === subdivisionId,
              });
            });

            return newSubdivisions;
          });
          document.getElementById("subdivisions-list")?.scrollTo({
            top: 0,
            behavior: "smooth",
          });
        } else
          setSubdivisions((prev) => {
            const newSubdivisions = new Map(prev);
            const current = newSubdivisions.get(subdivisionId);
            const updated = {
              ...current,
              ...newData,
            };
            if (JSON.stringify(current) === JSON.stringify(updated)) {
              return prev;
            }
            newSubdivisions.set(subdivisionId, updated);
            return newSubdivisions;
          });
      }
    },
    []
  );

  // Function to delete a specific subdivision
  const deleteSubdivision = useCallback((id) => {
    setSubdivisions((prev) => {
      if (!prev.has(id)) return prev;
      const newSubdivisions = new Map(prev);
      newSubdivisions.delete(id);
      return newSubdivisions;
    });
  }, []);

  // Function to expand only one subdivision
  const expandOnlySubdivision = useCallback((idToExpand) => {
    setSubdivisions((prev) => {
      const newSubdivisions = new Map(prev);

      prev.forEach((value, id) => {
        newSubdivisions.set(id, {
          ...value,
          expanded: id === idToExpand,
        });
      });

      return newSubdivisions;
    });
    document.getElementById("subdivisions-list")?.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  const value = useMemo(
    () => ({
      subdivisions,
      setSubdivisions,
      updateSubdivision,
      deleteSubdivision,
      expandOnlySubdivision,
    }),
    [
      subdivisions,
      setSubdivisions,
      updateSubdivision,
      deleteSubdivision,
      expandOnlySubdivision,
    ]
  );

  return (
    <SubdivisionContext.Provider value={value}>
      {children}
    </SubdivisionContext.Provider>
  );
};

export const useSubdivisionContext = (id) => {
  const {
    subdivisions,
    setSubdivisions,
    updateSubdivision,
    deleteSubdivision,
    expandOnlySubdivision,
  } = useContext(SubdivisionContext);
  return {
    subdivision: subdivisions.get(id) || null,
    setSubdivisions,
    updateSubdivision,
    deleteSubdivision,
    expandOnlySubdivision,
  };
};

export const useUpdateSubdivision = () => {
  return useContext(SubdivisionContext).updateSubdivision;
};

export const useSubdivisionsList = () => {
  return useContext(SubdivisionContext).subdivisions;
};

export const useExpandOnlySubdivision = () => {
  return useContext(SubdivisionContext).expandOnlySubdivision;
};
